import { useCallback, useEffect, useState } from "react";
import { useAuth } from "../lib/auth";
import { LiveEnvironment } from "./LiveEnvironment";
import { SectionHead } from "./SectionHead";

// The Chief of Staff — who can do what, on the record. The permission matrix (accounts × the nine
// agent-named scopes) made visible; grant is direct + audited, revoke goes through a plan preview and
// refuses self-revoke and stripping the last chief_of_staff; 2FA posture with the mandatory-2FA gap
// flagged; break-glass status + a manual drill that escalates into the Floor Manager queue on failure.
export default function TheChiefOfStaff() {
  const { authedFetch } = useAuth();
  const [v, setV] = useState(null);
  const [me, setMe] = useState(null);
  const [err, setErr] = useState("");
  const [pending, setPending] = useState(null); // {staff_id, scope, email, preview}
  const [msg, setMsg] = useState("");

  const load = useCallback(() => {
    authedFetch("/chief-of-staff/section").then(setV).catch((e) => setErr(String(e)));
  }, [authedFetch]);
  useEffect(load, [load]);
  useEffect(() => { authedFetch("/identity/me").then(setMe).catch(() => {}); }, [authedFetch]);

  if (err) return <div className="gk-err" data-testid="cos-error">{err}</div>;
  if (!v) return <div className="loading" data-testid="cos-loading">Loading…</div>;

  const myId = me?.account_id;

  async function grant(staff_id, scope) {
    setMsg("");
    try { await authedFetch("/chief-of-staff/scope/grant", { method: "POST", body: { staff_id, scope } }); load(); }
    catch (e) { setMsg(String(e)); }
  }
  async function beginRevoke(staff_id, scope, email) {
    setMsg("");
    try {
      const preview = await authedFetch("/chief-of-staff/scope/revoke/preview", { method: "POST", body: { staff_id, scope } });
      setPending({ staff_id, scope, email, preview });
    } catch (e) { setMsg(String(e)); }
  }
  async function confirmRevoke() {
    setMsg("");
    try {
      await authedFetch("/chief-of-staff/scope/revoke", { method: "POST", body: { staff_id: pending.staff_id, scope: pending.scope } });
      setPending(null); load();
    } catch (e) { setMsg(String(e)); }
  }

  return (
    <div data-testid="the-chief-of-staff">
      <SectionHead eyebrow="THE CHIEF OF STAFF · CONSOLE STAFF" testid="cos"
        headline={`${v.staff.length} ${v.staff.length === 1 ? "person" : "people"} can get into the console.`}>
        <p className="gk-constraint" data-testid="cos-constraint">{v.constraint}</p>
      </SectionHead>

      <div className="gk-panel" data-testid="cos-matrix-panel">
        <h3>Staff &amp; scopes <span className="gk-tag">permission matrix — server-side</span></h3>
        <p className="gk-muted">Click a cell to grant (direct, on the record) or revoke (a revoke removes power — it goes through a plan preview).</p>
        <div className="cos-matrix-scroll">
          <table className="gk-table cos-matrix" data-testid="cos-matrix">
            <thead>
              <tr>
                <th className="cos-acct-h">account</th>
                {v.scopes.map((s) => <th key={s} className="cos-scope-h"><span>{s}</span></th>)}
                <th>2FA</th>
              </tr>
            </thead>
            <tbody>
              {v.staff.map((u) => (
                <tr key={u.staff_id} data-testid={`cos-row-${u.staff_id}`}>
                  <td className="cos-acct">
                    <div className="cos-email mono">{u.email}</div>
                    <div className="cos-flags">
                      {u.is_master && <span className="held-kind" data-testid={`cos-master-${u.staff_id}`}>master</span>}
                      {u.is_breakglass && <span className="held-kind">break-glass</span>}
                      {myId === u.staff_id && <span className="held-kind">you</span>}
                    </div>
                  </td>
                  {v.scopes.map((s) => {
                    const held = u.scopes.includes(s);
                    const isSelf = myId === u.staff_id;
                    return (
                      <td key={s} className="cos-cell">
                        <button
                          className={`cos-dot ${held ? "held" : "empty"} ${isSelf && held ? "self" : ""}`}
                          data-testid={`cos-cell-${u.staff_id}-${s}`}
                          title={held ? (isSelf ? "you cannot revoke your own scope" : `revoke ${s}`) : `grant ${s}`}
                          onClick={() => held ? beginRevoke(u.staff_id, s, u.email) : grant(u.staff_id, s)}>
                          {held ? "✓" : "–"}
                        </button>
                      </td>
                    );
                  })}
                  <td><TwoFA t={u.twofa} sid={u.staff_id} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="gk-muted">{v.twofa_note}</p>
        {msg && <div className="gk-msg" data-testid="cos-msg">{msg}</div>}
      </div>

      {pending && (
        <div className="st-edit-box st-preview cos-revoke-preview" data-testid="cos-revoke-preview">
          {pending.preview.would_block
            ? <>
                <span className="gk-warn-text" data-testid="cos-revoke-blocked">Refused — {pending.preview.block_reason}</span>
                <button className="linkbtn" data-testid="cos-revoke-cancel" onClick={() => setPending(null)}>Close</button>
              </>
            : <>
                <span className="st-diff">Revoke <strong>{pending.scope}</strong> from <span className="mono">{pending.email}</span>
                  {pending.scope === "chief_of_staff" && ` — chief_of_staff holders after: ${pending.preview.chief_of_staff_holders_after}`}</span>
                <button className="act act-approve" data-testid="cos-revoke-confirm" onClick={confirmRevoke}>Confirm revoke</button>
                <button className="linkbtn" data-testid="cos-revoke-cancel" onClick={() => setPending(null)}>Cancel</button>
              </>}
        </div>
      )}

      <BreakGlass bg={v.breakglass} authedFetch={authedFetch} reload={load} />
      <OurAccounts authedFetch={authedFetch} />
    </div>
  );
}

// Which accounts are OURS (founder June 2026). The flag keeps a test account out of the Treasurer's
// funnel and the Rainmaker's cohorts. Staff-only, audit-logged server-side, and never shown to an owner.
function OurAccounts({ authedFetch }) {
  const [v, setV] = useState(null);
  const [reason, setReason] = useState({});
  const [msg, setMsg] = useState("");

  const load = useCallback(() => {
    authedFetch("/identity/staff/accounts").then(setV).catch((e) => setMsg(String(e)));
  }, [authedFetch]);
  useEffect(load, [load]);

  async function set(accountId, internal) {
    setMsg("");
    try {
      await authedFetch(`/identity/staff/account/${accountId}/internal`, {
        method: "POST", body: { internal, reason: internal ? (reason[accountId] || "") : "" },
      });
      setReason((r) => ({ ...r, [accountId]: "" }));
      load();
    } catch (e) { setMsg(e.detail || String(e)); }
  }

  if (!v) return <div className="gk-panel" data-testid="cos-ours-loading">Loading accounts…</div>;
  const ours = v.accounts.filter((a) => a.internal).length;
  return (
    <div className="gk-panel" data-testid="cos-ours">
      <h3>Whose account is this? <span className="gk-tag">{ours} of {v.count} marked ours</span></h3>
      <p className="gk-muted">An account marked ours is left out of the Treasurer&apos;s funnel and the
        Rainmaker&apos;s cohorts. A reason is required, the change is on the record, and the owner is
        never told.</p>
      <table className="gk-table" data-testid="cos-ours-table">
        <thead><tr><th>email</th><th>buildings</th><th>files</th><th>ours</th><th></th></tr></thead>
        <tbody>
          {v.accounts.map((a) => (
            <tr key={a.account_id} data-testid={`cos-ours-row-${a.account_id}`}>
              <td className="mono">{a.email || a.account_id}</td>
              <td>{a.buildings}</td>
              <td>{a.files}</td>
              <td>
                {a.internal
                  ? <span className="status-pill sp-ours" data-testid={`cos-ours-flag-${a.account_id}`}>
                      ours — {a.internal_reason || "no reason on the record"}
                    </span>
                  : <span className="gk-muted">a real owner</span>}
              </td>
              <td>
                {a.internal
                  ? <button className="linkbtn" data-testid={`cos-ours-clear-${a.account_id}`}
                            onClick={() => set(a.account_id, false)}>Clear</button>
                  : <span className="cos-ours-set">
                      <input className="intent-input cos-ours-reason" placeholder="reason — required"
                             data-testid={`cos-ours-reason-${a.account_id}`}
                             value={reason[a.account_id] || ""}
                             onChange={(e) => setReason((r) => ({ ...r, [a.account_id]: e.target.value }))} />
                      <button className="act act-approve" data-testid={`cos-ours-set-${a.account_id}`}
                              disabled={!(reason[a.account_id] || "").trim()}
                              onClick={() => set(a.account_id, true)}>Mark as ours</button>
                    </span>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {msg && <div className="gk-msg" data-testid="cos-ours-msg">{msg}</div>}
    </div>
  );
}

function TwoFA({ t, sid }) {
  return (
    <div className="cos-2fa" data-testid={`cos-2fa-${sid}`}>
      <div className="cos-2fa-factor">{t.factor}</div>
      {t.gap
        ? <span className="status-pill sp-needs-human" data-testid={`cos-2fa-gap-${sid}`}>mandatory 2FA missing</span>
        : <span className="cos-2fa-ok">
            2FA active{!t.totp_enrolled && t.sms_primary ? " · authenticator backup not yet enrolled" : ""}
          </span>}
    </div>
  );
}

function BreakGlass({ bg, authedFetch, reload }) {
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [mint, setMint] = useState(null); // {code, digest}
  const [mintBusy, setMintBusy] = useState(false);
  const [mintErr, setMintErr] = useState("");
  const [copied, setCopied] = useState("");

  async function runDrill() {
    setBusy(true); setMsg("");
    try {
      const r = await authedFetch("/identity/staff/breakglass/drill");
      setRes(r); reload();
    } catch (e) { setMsg(String(e)); }
    setBusy(false);
  }

  async function mintCode() {
    setMintBusy(true); setMintErr(""); setMint(null); setCopied("");
    try {
      const r = await authedFetch("/identity/staff/breakglass/mint-code", { method: "POST" });
      setMint(r);
    } catch (e) { setMintErr(e.detail || String(e)); }
    setMintBusy(false);
  }

  async function copy(which, value) {
    setCopied(which);
    try { await navigator.clipboard.writeText(value); } catch (_) { /* label already flipped */ }
  }

  if (!bg.present) {
    return (
      <div className="gk-panel" data-testid="cos-breakglass">
        <h3>Break-glass</h3>
        <div className="status-pill sp-rejected" data-testid="cos-bg-absent">no break-glass account exists</div>
      </div>
    );
  }
  const Row = ({ label, ok, bad, testid }) => (
    <div className="cos-bg-row" data-testid={testid}>
      <span className="cos-bg-k">{label}</span>
      <span className={`status-pill ${bad ? "sp-rejected" : ok ? "sp-approved" : "sp-unscored"}`}>
        {bad ? "no" : ok ? "yes" : "—"}
      </span>
    </div>
  );

  return (
    <div className="gk-panel" data-testid="cos-breakglass">
      <h3>Break-glass <span className="gk-tag mono">{bg.email}</span></h3>
      <p className="gk-muted">The last-resort second master. It must be authenticator-primary (never SMS —
        a shared failure mode is the whole point of the split) with a sealed, unburned recovery code. The
        drill exercises the recovery mechanism on a throwaway canary — proving the drill path is reachable,
        not that the sealed recovery code would succeed (verifying it would burn it). A failure escalates
        into the Floor Manager queue. This is a drill, not the LLM "liveness" probe — a different thing.</p>
      <div className="cos-bg-grid">
        <Row label="authenticator-primary (never SMS)" ok={bg.authenticator_primary} bad={!bg.authenticator_primary} testid="cos-bg-authprimary" />
        <Row label="recovery code sealed" ok={bg.recovery_sealed} bad={!bg.recovery_sealed} testid="cos-bg-sealed" />
        <Row label="recovery code unburned" ok={!bg.recovery_used} bad={bg.recovery_used} testid="cos-bg-unburned" />
        <Row label="password set" ok={bg.password_set} bad={!bg.password_set} testid="cos-bg-password" />
      </div>
      {bg.open_drill_failures > 0 && (
        <div className="status-pill sp-needs-human" data-testid="cos-bg-openfail">
          {bg.open_drill_failures} open drill-failure escalation(s) in the Floor Manager queue
        </div>
      )}
      <div className="action-bar">
        <button className="act" data-testid="cos-bg-run-drill" disabled={busy} onClick={runDrill}>
          {busy ? "Running drill…" : "Run break-glass drill"}
        </button>
      </div>
      {res && (
        <div className={`gk-recorded-note ${res.ok ? "" : "cos-drill-fail"}`} data-testid="cos-bg-drill-result">
          {res.ok
            ? <>Drill passed — the canary round-trip completed. The sealed recovery code is
                <strong> unverified by construction</strong>: verifying it would burn it. What this proves
                is that the drill path is <strong>reachable</strong>, not that recovery would succeed.
                Checked {String(res.checked_at).slice(0, 19)}Z.</>
            : <span className="gk-warn-text">Drill FAILED — escalated into the Floor Manager queue. {(res.reasons || []).join("; ")}</span>}
        </div>
      )}
      {msg && <div className="gk-msg" data-testid="cos-bg-msg">{msg}</div>}

      {bg.recovery_mint_available && (
      <div className="cos-bg-mint" data-testid="cos-bg-mint">
        <h4>Recovery code setup <span className="gk-tag">preview only</span></h4>
        <p className="gk-muted">Generate the sealed break-glass recovery code here, in preview, so the
          plaintext appears only in this browser — never in a shared shell. Write the code on paper and
          keep it offline. Paste ONLY the digest into the production Secrets store as
          <span className="mono"> STAFF_BREAKGLASS_RECOVERY_CODE_DIGEST</span> (production refuses to
          start without it). Nothing is stored here — regenerating simply mints a new pair, so deploy the
          digest that matches the code you sealed. Minting is disabled in production by design, so
          rotating the code later — after a burn or a lost copy — must ALSO be done from preview, then
          update the prod digest.</p>
        <div className="action-bar">
          <button className="act" data-testid="cos-bg-mint-btn" disabled={mintBusy} onClick={mintCode}>
            {mintBusy ? "Generating…" : mint ? "Generate a new code" : "Generate recovery code"}
          </button>
        </div>
        {mintErr && <div className="gk-msg" data-testid="cos-bg-mint-err">{mintErr}</div>}
        {mint && (
          <div className="cos-bg-reveal" data-testid="cos-bg-mint-reveal">
            <div className="cos-reveal-row">
              <div className="cos-reveal-k">Recovery code — seal offline now, shown once</div>
              <div className="cos-reveal-v mono" data-testid="cos-bg-mint-code">{mint.code}</div>
              <button className="linkbtn" data-testid="cos-bg-mint-copy-code"
                      onClick={() => copy("code", mint.code)}>
                {copied === "code" ? "Copied" : "Copy"}
              </button>
            </div>
            <div className="cos-reveal-row">
              <div className="cos-reveal-k">Digest — paste into prod Secrets (STAFF_BREAKGLASS_RECOVERY_CODE_DIGEST)</div>
              <div className="cos-reveal-v mono cos-reveal-digest" data-testid="cos-bg-mint-digest">{mint.digest}</div>
              <button className="linkbtn" data-testid="cos-bg-mint-copy-digest"
                      onClick={() => copy("digest", mint.digest)}>
                {copied === "digest" ? "Copied" : "Copy"}
              </button>
            </div>
            <p className="gk-muted" data-testid="cos-bg-mint-note">The code is not stored anywhere. If you
              lose it before sealing, generate again and use the new digest. Leaving or reloading this page
              discards it for good.</p>
          </div>
        )}
      </div>
      )}
      <LiveEnvironment />
    </div>
  );
}
